import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppRefreshAndExport from '@/components/AppRefreshAndExport.vue'
import AppSearchBtn from '@/components/AppSearchBtn.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { showErrors, showNotification } from '@/utils/snack'
import ListPage from '@/mixins/list-page'
import GoodsDialog from './script-dialog'
import { findGoods, changeGoodsStatus, openOrCloseStore, exportGoodsList } from './gql'
import { SnackType } from '@/interfaces/snack'
import { Goods, GoodsOrderBy } from '@/interfaces/goods'
import { TypedGqlError } from '@/interfaces/error'
import AuthFilter from '@/mixins/auth-filter'
import { formatCurrency } from '@/utils/currency'
import { downloadBase64 } from '@/helpers/base64-to-file'

@Component({
  components: {
    AppBreadcrumbs,
    AppRefreshAndExport,
    AppDialog,
    AppSimpleTextField,
    AppSimpleSelector,
    AppDatePicker,
    AppSearchBtn,
    AppSelector,
    AppTextField
  }
})

export default class GoodsList extends mixins(ListPage, GoodsDialog, AuthFilter) {
  @State(state => state.page.GoodsList) cache!: string
  breadcrumbs: Breadcrumb[] = [
    {
      text: '商品管理'
    },
    {
      text: '商品品类',
      to: { name: 'GoodsList' }
    }
  ]

  // 排序
  options = {
    sortBy: ['createdAt'],
    page: 1,
    itemsPerPage: 10,
    sortDesc: [true]
  }

  // 搜索
  filter = {
    title: '',
    statusIndex: -1,
    isbn: ''
  }

  totalNum = 0
  loading = false

  headers = [
    { text: '封面', value: 'thumb', sortable: false },
    { text: 'ISBN', value: 'isbn' },
    { text: '商品标题', value: 'title' },
    { text: '作者', value: 'author' },
    { text: '出版社', value: 'press' },
    { text: '状态', value: 'status' },
    { text: '价格', value: 'price' },
    { text: '添加时间', value: 'createdAt' },
    { text: '添加人', value: 'managers.createdBy.username', sortable: false },
    { text: '操作', value: 'actions', sortable: false, align: 'end' }
  ]

  goods: Goods[] = []

  selectedGoods = []
  disabledActions = false
  formatCurrency = formatCurrency

  isOn = false

  windowSize = {
    w: 0,
    h: 0
  }

  actionsHeight = 51

  get showSelect () {
    if (this.getRole() === 'dealer') {
      return false
    } else {
      return true
    }
  }

  mounted () {
    this.onResize()
    this.restorePage(this.cache)
    this.getGoods()
    this.isOn = !!this.me.dealer?.ifStoreOpening
    if (this.getRole() === 'dealer') {
      this.headers.pop()
    }
  }

  @Watch('options', { deep: true })
  onOptionsChanged () {
    this.getGoods()
  }

  @Watch('selectedGoods', { deep: true, immediate: true })
  selectGoods (val: any) {
    if (val.length > 0) { this.disabledActions = false } else {
      this.disabledActions = true
    }
  }

  // 刷新列表
  async refreshList () {
    this.filter = {
      title: '',
      statusIndex: -1,
      isbn: ''
    }
    await this.getGoods()
  }

  // 获取商品
  async getGoods () {
    const { sortBy, page, itemsPerPage, sortDesc } = this.options
    const { title, statusIndex, isbn } = this.filter
    const by = GoodsOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0] || false

    this.loading = true
    const order = { by, asc }
    const paging = {
      skip: itemsPerPage * (page - 1),
      take: itemsPerPage
    }
    const where = {
      title: title || null,
      isbn: isbn || null,
      status_EQ: this.statusCodes[statusIndex] || null
    }
    const result = await this.queryGoodsRequest(order, paging, where)
    if (result) {
      this.goods = result.goods
      this.totalNum = result.paging.totalCount
    }
    this.loading = false
    this.storePage()
  }

  // 搜索
  async filterQuerySubmit () {
    await this.getGoods()
  }

  // 确认删除
  async deleteDialogConfirm () {
    await this.deleteGoodsRequest()
    await this.getGoods()
  }

  // 详情
  routeToDetail (item: Goods) {
    this.targetgoods = item
    this.$router.push({
      name: 'GoodsDetail',
      params: {
        id: item.id + '',
        goodsName: item.title
      }
    })
  }

  // 批量修改状态
  async updateStatusGoods (val: boolean) {
    const msg = val ? '上架' : '下架'
    const goodIds = this.selectedGoods.map((item: Goods) => {
      return item.id
    })
    const status = val ? this.GoodsStatus.ON_SHELVES : this.GoodsStatus.OFF_SHELVES
    const result = await this.$apollo.mutate({
      mutation: changeGoodsStatus,
      variables: {
        goodIds,
        status
      }
    })
    if (result) {
      showNotification({
        msg: `${msg}成功！`,
        type: SnackType.Success
      })
      this.selectedGoods = []
      await this.getGoods()
    } else {
      showNotification({
        msg: `${msg}失败！`,
        type: SnackType.Error
      })
    }
  }

  async queryGoodsRequest (order: any, paging: any, where: any) {
    try {
      const result = await this.$apollo.query({
        query: findGoods,
        variables: {
          order,
          paging,
          where
        }

      })
      return result.data.findGoods
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  // 更改经销商小店开店状态
  async updateShopStatus (isOpening: boolean) {
    try {
      const result = await this.$apollo.mutate({
        mutation: openOrCloseStore,
        variables: {
          dealerId: this.me.dealer?.id,
          isOpening
        }
      })
      if (result) {
        const msg = isOpening ? '营业成功！' : '打烊成功！'
        showNotification({
          msg: `${msg}`,
          type: SnackType.Success
        })
        this.isOn = isOpening
      }
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  // 导出
  async exportHandler () {
    showNotification({
      msg: '即将开始下载...',
      type: SnackType.Info
    })
    const { sortBy, sortDesc } = this.options
    const { title, statusIndex, isbn } = this.filter
    const by = GoodsOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0] || false
    this.loading = true
    const order = { by, asc }
    const where = {
      title: title || null,
      isbn: isbn || null,
      status_EQ: this.statusCodes[statusIndex] || null
    }
    try {
      const result = await this.$apollo.query({
        query: exportGoodsList,
        variables: {
          order,
          where
        }
      })
      if (result.data) {
        const { fileName, contentB64 } = result.data.exportGoodsList
        downloadBase64(contentB64, fileName)
      }
      this.loading = false
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
    const actionDom = this.$refs.actions as Vue & {
      clientHeight: number;
    }
    this.actionsHeight = actionDom.clientHeight
  }
}
